"use client"

import * as React from "react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"

const Accordion = AccordionPrimitive.Root

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn("border-b group/item", className)}
    {...props}
  />
))
AccordionItem.displayName = "AccordionItem"

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline text-left [&[data-state=open]>svg]:rotate-180",
        className
      )}
      {...props}
    >
      {children}
      <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & { forceMount?: true }
>(({ className, children, forceMount, ...props }, ref) => {
  if (forceMount) {
    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        className="grid grid-rows-[0fr] group-data-[state=open]/item:grid-rows-[1fr] transition-[grid-template-rows] duration-200 ease-out text-sm"
      >
        <div className="overflow-hidden">
          <div className={cn("pb-4 pt-0", className)}>{children}</div>
        </div>
      </div>
    )
  }

  return (
    <AccordionPrimitive.Content
      ref={ref}
      className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      {...props}
    >
      <div className={cn("pb-4 pt-0", className)}>{children}</div>
    </AccordionPrimitive.Content>
  )
})
AccordionContent.displayName = AccordionPrimitive.Content.displayName

// AccordionTriggerH2 - Uses h2 for FAQ sections (SEO)
const AccordionTriggerH2 = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      asChild
      className={cn(
        "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:no-underline text-left [&[data-state=open]>svg]:rotate-180",
        className
      )}
      {...props}
    >
      <h2 className="flex items-center justify-between w-full">
        {children}
        <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
      </h2>
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))
AccordionTriggerH2.displayName = "AccordionTriggerH2"

export {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionTriggerH2,
  AccordionContent,
}
